/**
 * WizardStepReview (Step 6)
 *
 * Final review before creating permit applications.
 * Shows summary of all entered data and creates jobs.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { dualWriteJob } from '@/lib/firestore-helpers';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Loader2,
  MapPin,
  Briefcase,
  FileCheck,
  DollarSign,
  User,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import type { WizardState } from '@/types/wizard';

interface WizardStepReviewProps {
  state: WizardState;
  onComplete: (projectId: string, jobIds: string[]) => void;
}

export function WizardStepReview({ state, onComplete }: WizardStepReviewProps) {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);

  // Get contractor ID
  const contractorId = profile?.contractorId || user?.uid;

  // Determine what will be created
  const jobsToCreate = useMemo(() => {
    const jobs: Array<{
      title: string;
      permitType: string;
      tradeCode: string;
      tradeName: string;
      scopeName?: string;
    }> = [];

    // Create a job for each accepted permit
    state.permits.recommendations
      .filter((rec) => rec.userAccepted)
      .forEach((rec) => {
        const tradeScope = state.scopes.tradeScopes.find(
          (ts) => ts.tradeCode === rec.tradeCode
        );

        // Get scope names from selected scopes array
        const scopeNames = tradeScope?.selectedScopes?.map((s) => s.scopeName).join(', ');

        jobs.push({
          title: `${rec.permitName} - ${state.basics.address?.street || 'New Permit'}`,
          permitType: rec.permitCode,
          tradeCode: rec.tradeCode || '',
          tradeName: tradeScope?.tradeName || rec.tradeCode || '',
          scopeName: scopeNames,
        });
      });

    // If no permits selected but we have scopes, create jobs for each trade
    if (jobs.length === 0 && state.scopes.tradeScopes.length > 0) {
      state.scopes.tradeScopes.forEach((ts) => {
        // Get scope names from selected scopes array
        const scopeNames = ts.selectedScopes?.map((s) => s.scopeName).join(', ');

        jobs.push({
          title: `${ts.tradeName} Permit - ${state.basics.address?.street || 'New Permit'}`,
          permitType: ts.tradeCode,
          tradeCode: ts.tradeCode,
          tradeName: ts.tradeName,
          scopeName: scopeNames,
        });
      });
    }

    return jobs;
  }, [state]);

  // Validation
  const blockers = useMemo(() => {
    const issues: string[] = [];

    if (!state.basics.address?.street) {
      issues.push('Address is required');
    }
    if (!state.basics.jurisdictionId) {
      issues.push('Jurisdiction could not be determined');
    }
    if (!state.basics.propertyType) {
      issues.push('Property type is required');
    }
    if (!state.basics.category) {
      issues.push('Project type is required');
    }
    if (!state.basics.contractorRole) {
      issues.push('Contractor role is required');
    }
    if (state.trades.selectedTradeIds.length === 0) {
      issues.push('At least one trade must be selected');
    }
    if (jobsToCreate.length === 0) {
      issues.push('No permits to create');
    }

    return issues;
  }, [state, jobsToCreate]);

  const warnings = useMemo(() => {
    const warns: string[] = [];

    if (state.permits.rejectedPermitIds.length > 0) {
      warns.push('Some recommended permits were declined');
    }
    if (!state.details.estimatedValue) {
      warns.push('Project value not specified - NOC requirements may apply');
    }

    return warns;
  }, [state]);

  const canCreate = blockers.length === 0;

  // Generate a unique job ID
  const generateJobId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 11);
    return `job_${timestamp}_${random}`;
  };

  // Create jobs in Firestore
  const handleCreate = async () => {
    if (!contractorId || !canCreate) return;

    setIsCreating(true);

    try {
      const createdJobIds: string[] = [];

      // Create each job using dualWriteJob to write to both old and new structure
      for (const job of jobsToCreate) {
        const jobId = generateJobId();

        const jobData = {
          id: jobId,
          contractorId: contractorId, // Required for Firestore security rules
          title: job.title,
          description: job.scopeName || '',
          location: state.basics.address?.formatted || state.basics.address?.street || '',
          status: 'not_started',
          permitType: job.permitType,
          jurisdictionId: state.basics.jurisdictionId || null,
          propertyType: state.basics.propertyType || null,
          projectType: state.basics.category || null,
          estimatedValue: state.details.estimatedValue || null,
          isOwnerBuilder: state.details.isOwnerBuilder ?? null,
          // Client info
          client_name: state.details.client?.name || '',
          client_email: state.details.client?.email || '',
          client_phone: state.details.client?.phone || '',
          // Commercial metadata
          commercialMetadata: state.basics.propertyType === 'commercial'
            ? state.details.commercialMetadata || null
            : null,
          // Wizard metadata
          createdViaWizard: true,
          wizardSessionId: state.id,
          // Timestamps
          createdAt: Timestamp.now(),
          createdBy: user?.uid || null,
        };

        // Use dualWriteJob to write to both old (jobs/{id}) and new (contractors/{cId}/jobs/{id}) structure
        await dualWriteJob(contractorId, jobId, jobData);
        createdJobIds.push(jobId);
      }

      toast({
        title: 'Success!',
        description: `Created ${createdJobIds.length} permit application${createdJobIds.length !== 1 ? 's' : ''}`,
      });

      // Call completion handler
      onComplete('', createdJobIds);
    } catch (error) {
      console.error('Failed to create jobs:', error);
      toast({
        title: 'Error',
        description: 'Failed to create permit applications. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Blockers */}
      {blockers.length > 0 && (
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-red-600 dark:text-red-300">
              <AlertTriangle className="h-5 w-5" />
              Cannot Create Yet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {blockers.map((blocker, i) => (
                <li key={i} className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                  <span>•</span> {blocker}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Warnings */}
      {warnings.length > 0 && blockers.length === 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-amber-600 dark:text-amber-300">
              <AlertTriangle className="h-5 w-5" />
              Warnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {warnings.map((warning, i) => (
                <li key={i} className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
                  <span>•</span> {warning}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Project Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Project Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Address:</span>
              <p className="font-medium">{state.basics.address?.formatted || state.basics.address?.street || '-'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Jurisdiction:</span>
              <div className="flex items-center gap-2">
                <p className="font-medium">{state.basics.jurisdictionName || '-'}</p>
                {state.basics.jurisdictionId && (
                  <ConfidenceBadge
                    level="high"
                    source="Address geocoding"
                    showLabel={false}
                  />
                )}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">Project Type:</span>
              <p className="font-medium capitalize">{state.basics.category?.replace('_', ' ') || '-'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Property Type:</span>
              <p className="font-medium capitalize">{state.basics.propertyType || '-'}</p>
            </div>
          </div>

          {/* Special zones - auto-detected */}
          {(state.basics.isHvhz || state.basics.isFloodZone || state.basics.isCoastalZone) && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Sparkles className="h-3 w-3" />
                Auto-detected zones:
              </div>
              <div className="flex flex-wrap gap-2">
                {state.basics.isHvhz && (
                  <div className="flex items-center gap-1">
                    <Badge variant="secondary" className="text-xs">
                      <AlertTriangle className="h-3 w-3 mr-1 text-amber-500" />
                      HVHZ
                    </Badge>
                    <ConfidenceBadge level="high" source="Address geocoding" showLabel={false} />
                  </div>
                )}
                {state.basics.isFloodZone && (
                  <div className="flex items-center gap-1">
                    <Badge variant="secondary" className="text-xs">
                      Flood Zone
                    </Badge>
                    <ConfidenceBadge level="high" source="Address geocoding" showLabel={false} />
                  </div>
                )}
                {state.basics.isCoastalZone && (
                  <div className="flex items-center gap-1">
                    <Badge variant="secondary" className="text-xs">
                      Coastal Zone
                    </Badge>
                    <ConfidenceBadge level="high" source="Address geocoding" showLabel={false} />
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permits to Create */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Permits to Create ({jobsToCreate.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {jobsToCreate.length === 0 ? (
            <p className="text-sm text-muted-foreground">No permits configured</p>
          ) : (
            <div className="space-y-3">
              {jobsToCreate.map((job, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <FileCheck className="h-5 w-5 text-primary" />
                  <div className="flex-1">
                    <p className="font-medium text-sm">{job.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {job.tradeName}
                      </Badge>
                      {job.scopeName && (
                        <span className="text-xs text-muted-foreground">
                          {job.scopeName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Additional Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Additional Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Estimated Value:</span>
            <span className="font-medium">
              {state.details.estimatedValue
                ? `$${state.details.estimatedValue.toLocaleString()}`
                : 'Not specified'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Owner-Builder:</span>
            <span className="font-medium">
              {state.details.isOwnerBuilder === true
                ? 'Yes'
                : state.details.isOwnerBuilder === false
                ? 'No'
                : 'Not specified'}
            </span>
          </div>
          {state.details.client?.name && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Client:</span>
              <span className="font-medium">{state.details.client.name}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conditional Requirements */}
      {state.details.conditionalRequirements.filter((r) => r.triggered).length > 0 && (
        <Card className="border-amber-200 dark:border-amber-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-amber-600 dark:text-amber-300">
              <AlertTriangle className="h-5 w-5" />
              Conditional Requirements
              <span className="text-xs font-normal text-muted-foreground flex items-center gap-1 ml-auto">
                <Sparkles className="h-3 w-3" />
                Auto-detected
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {state.details.conditionalRequirements
                .filter((r) => r.triggered)
                .map((req) => (
                  <li key={req.id} className="text-sm flex items-center gap-2">
                    <Badge variant="outline" className="text-xs shrink-0">
                      {req.type.toUpperCase()}
                    </Badge>
                    <span className="flex-1">{req.name}</span>
                    <ConfidenceBadge
                      level="high"
                      source={req.triggerReason}
                      showLabel={false}
                    />
                  </li>
                ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Separator />

      {/* Create Button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {canCreate
            ? `Ready to create ${jobsToCreate.length} permit application${jobsToCreate.length !== 1 ? 's' : ''}`
            : 'Please resolve all issues before creating'}
        </p>

        <Button
          size="lg"
          disabled={!canCreate || isCreating}
          onClick={handleCreate}
          className="min-w-[200px]"
        >
          {isCreating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Creating...
            </>
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Create Permit{jobsToCreate.length !== 1 ? 's' : ''}
              <ArrowRight className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
